import { Grid } from "@mui/material";
import React from "react";
import { ApplicationPermissionType, DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as recordService from "../../../models/services/recordService";
import { ISessionState } from "../../../models/session/sessionState";
import { IUnitModuleNode } from "../../../models/unit/unitModuleNode";
import { IUnitNodePermissions } from "../../../models/unit/unitNodePermissions";
import { Dialog } from "../../framework/Dialog/Dialog";
import { GridRow } from "../../framework/Grid/GridRow";
import { IPageNavMenuItem, PageNavMenuItems } from "../../framework/PageNav/PageNavMenuItems";
import { UnitModuleSettings } from "../../unit/Unit/UnitModuleSettings";
import { RecordEducationList } from "./RecordEducationList";
import { RecordFamiliarizationList } from "./RecordFamiliarizationList";
import { RecordFurtherEducationList } from "./RecordFurtherEducationList";
import { ModulePartnersSettings } from "source/components/modulePartner";
import { RecordSettings, TRecordSettingsRefProps } from "./RecordSettings";
import { UnitPermissionSettingsNew } from "source/components/unit/Unit/UnitPermissionSettingsNew/UnitPermissionSettingsNew";

/* eslint-disable no-unused-vars */
export const enum RecordSettingsDialogMenuItem {
    Settings = "0",
    Units = "1",
    Permissions = "2",
    Familiarizations = "3",
    BasicEducations = "4",
    FurtherEducations = "5",
    RecordUserPartners = "6",
    DefaultFamiliarizations = "7",
    DefaultBasicEducations = "8",
    DefaultFurtherEducations = "9",
}
/* eslint-enable no-unused-vars */

export interface IRecordSettingsDialogProp {
    sessionState: ISessionState;
    companyId: string;
    classes?: string;
    settingsActions: IPageNavMenuItem[];
    onClose: () => void;
}

export interface IRecordSettingsDialogState {
    // Units
    unitModuleNode: IUnitModuleNode;
    // Permissions
    unitNodePermissions: IUnitNodePermissions;
    activeMenuItem: RecordSettingsDialogMenuItem;
}

export default class RecordSettingsDialog extends React.Component<IRecordSettingsDialogProp, IRecordSettingsDialogState> {
    private unitsRef = React.createRef<UnitModuleSettings>();
    private permissionsRef = React.createRef<UnitPermissionSettingsNew>();
    private settingsRef = React.createRef<TRecordSettingsRefProps>();

    unitsPageAllowed = (props: IRecordSettingsDialogProp) => !!props.settingsActions.find(i => i.id === RecordSettingsDialogMenuItem.Units);

    constructor(props: IRecordSettingsDialogProp) {
        super(props);
        this.state = {
            unitModuleNode: null,
            unitNodePermissions: null,
            activeMenuItem: this.unitsPageAllowed(props)
                ? RecordSettingsDialogMenuItem.Units
                : RecordSettingsDialogMenuItem.Familiarizations
        };
    }

    async componentDidMount(): Promise<void> {
        if (this.unitsPageAllowed(this.props)) {
            await this.loadUnits();
        }
    }

    // #region Units
    onUnitModuleSettingsCancel = async (): Promise<boolean> => {
        return await this.unitsRef.current.handleCancel(false);
    };

    loadUnits = async () => {
        const result = await AppUtils.callService(() => recordService.getUnits(this.props.companyId), false);
        if (!result) return;
        this.setState({
            unitModuleNode: result.unitModuleNode
        });
    };

    handleUnitModuleSettingsSave = async (obj: any) => {
        return await recordService.saveUnits(obj);
    };
    // #endregion Units
    
    // #region Permissions
    onUnitPermissionSettingsCancel = async (): Promise<boolean> => {
        return await this.permissionsRef.current.handleCancel(false);
    };

    loadPermissions = async () => {
        const result = await AppUtils.callService(() => recordService.getUnitNodePermissions(this.props.companyId), false);
        if (!result) return;
        this.setState({
            unitNodePermissions: result
        });
    };

    handleUnitPermissionSettingsSave = async (obj: any) => {
        const result = await recordService.saveUnitNodePermissions(obj);
        this.loadPermissions();
        return result;
    };
    // #endregion Permissions

    // #region Record User Settings
    onRecordUserSettingsCancel = async (): Promise<boolean> => {
        return await this.settingsRef.current.cancel(false);
    };
    // #endregion Record User Settings

    handleToggleActiveMenuItem = async (menuItem: RecordSettingsDialogMenuItem) => {
        const state = this.state;
        if (state.activeMenuItem === menuItem) return;
        //Check current tab cancel
        if (state.activeMenuItem === RecordSettingsDialogMenuItem.Units) {
            if (!await this.onUnitModuleSettingsCancel()) return;
        } else if (state.activeMenuItem === RecordSettingsDialogMenuItem.Permissions) {
            if (!await this.onUnitPermissionSettingsCancel()) return;
        } else if (state.activeMenuItem === RecordSettingsDialogMenuItem.Settings) {
            if (!await this.onRecordUserSettingsCancel()) return;
        }
        //Load new tab data
        if (menuItem === RecordSettingsDialogMenuItem.Units) {
            await this.loadUnits();
        } else if (menuItem === RecordSettingsDialogMenuItem.Permissions) {
            await this.loadPermissions();
        }
        //Set new tab active
        this.setState({
            activeMenuItem: menuItem
        });
    };

    handleCancel = async (closeDialog: boolean) => {
        if (!closeDialog) return;
        this.props.onClose();
    };

    handleSubViewOk = (_closeDialog: boolean) => {
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Dialog
                id="record-settings-dialog"
                title={Translations.Settings}
                maxWidth="lg"
                maxHeight="100%"
                onClose={() => this.handleCancel(true)}
                body={
                    <GridRow flexGrow={2}>
                        <Grid item sm={3}>
                            <PageNavMenuItems
                                activeId={state.activeMenuItem}
                                items={props.settingsActions}
                                onToggleMenuItem={this.handleToggleActiveMenuItem}
                            />
                        </Grid>
                        <Grid item sm={9} display="flex" flexDirection="column" flexGrow={2}>
                            {state.activeMenuItem === RecordSettingsDialogMenuItem.Settings && (
                                <RecordSettings
                                    ref={this.settingsRef}
                                    companyId={props.companyId}
                                    onOk={this.handleSubViewOk}
                                    onCancel={this.handleCancel}
                                />
                            )}
                            {(state.activeMenuItem === RecordSettingsDialogMenuItem.Units) && (
                                <UnitModuleSettings
                                    ref={this.unitsRef}
                                    infoText={Translations.RecordUserModuleInfoUnits}
                                    companyId={props.companyId}
                                    sessionState={props.sessionState}
                                    applicationPermissionType={ApplicationPermissionType.Record}
                                    unitModuleNode={state.unitModuleNode}
                                    onCancel={this.handleCancel}
                                    onOk={this.handleSubViewOk}
                                    onSave={this.handleUnitModuleSettingsSave}
                                />
                            )}
                            {state.activeMenuItem === RecordSettingsDialogMenuItem.Permissions && (
                                <UnitPermissionSettingsNew
                                    ref={this.permissionsRef}
                                    infoText={Translations.RecordUserModuleInfoPermissions}
                                    companyId={props.companyId}
                                    unitNodePermissions={state.unitNodePermissions}
                                    cardView={true}
                                    onSave={this.handleUnitPermissionSettingsSave}
                                    onOk={this.handleSubViewOk}
                                    onCancel={this.handleCancel}
                                />
                            )}
                            {state.activeMenuItem === RecordSettingsDialogMenuItem.Familiarizations && (
                                <RecordFamiliarizationList
                                    companyId={props.companyId}
                                />
                            )}
                            {state.activeMenuItem === RecordSettingsDialogMenuItem.BasicEducations && (
                                <RecordEducationList
                                    companyId={props.companyId}
                                />
                            )}
                            {state.activeMenuItem === RecordSettingsDialogMenuItem.FurtherEducations && (
                                <RecordFurtherEducationList
                                    companyId={props.companyId}
                                />
                            )}
                            {state.activeMenuItem === RecordSettingsDialogMenuItem.DefaultFamiliarizations && (
                                <RecordFamiliarizationList
                                    companyId={null}
                                />
                            )}
                            {state.activeMenuItem === RecordSettingsDialogMenuItem.DefaultBasicEducations && (
                                <RecordEducationList
                                    companyId={null}
                                />
                            )}
                            {state.activeMenuItem === RecordSettingsDialogMenuItem.DefaultFurtherEducations && (
                                <RecordFurtherEducationList
                                    companyId={null}
                                />
                            )}
                            {state.activeMenuItem === RecordSettingsDialogMenuItem.RecordUserPartners &&
                                <ModulePartnersSettings
                                    databaseRecordType={DatabaseRecordType.RecordUser}
                                    companyId={props.companyId}
                                    canAddPartner={props.sessionState.isSuperAdmin}
                                    canRemovePartner={true}
                                    onOk={() => this.handleSubViewOk(true)}
                                />
                            }
                        </Grid>
                    </GridRow>
                }
                buttons={
                    state.activeMenuItem !== RecordSettingsDialogMenuItem.Settings &&
                    state.activeMenuItem !== RecordSettingsDialogMenuItem.Units &&
                    state.activeMenuItem !== RecordSettingsDialogMenuItem.Permissions
                        ? [{ title: Translations.Close, variant: "outlined", onClick: () => this.handleCancel(true) }]
                        : null
                }
            />
        );
    }
}
